import { z } from 'zod';
import { amountInputSchema, amountSchema } from '../common/amount.js';
import { paginationMetaSchema } from '../common/pagination.js';
import { isoDateTimeSchema } from '../common/primitives.js';
import { branchRefSchema, employeeRefSchema } from '../common/refs.js';
import { accountSchema } from './accounts.js';
import { debtorSchema } from './debtors.js';
import { enteredMovementKindSchema, exportLocaleShape, messageStatusSchema, movementKindFilterSchema, movementKindSchema, periodIsOrdered, periodQueryShape, PERIOD_ORDER_ISSUE, } from './common.js';
/** الحركات and كشف العميل (debts.md sections 7 and 8). */
export const STATEMENT_MAX = 255;
export const NOTES_MAX = 1000;
/** The statement opens on its last page, 20 rows a page (debts.md section 7). */
export const STATEMENT_PAGE_SIZE = 20;
/** البيان: one line of free text, up to 255 characters; empty means none. */
export const movementStatementSchema = z
    .string()
    .trim()
    .max(STATEMENT_MAX)
    .refine((v) => !/[\r\n]/.test(v), { message: 'statement must be one line' })
    .transform((v) => (v === '' ? null : v));
/** ملاحظات: free text up to 1,000 characters; empty means none. */
export const movementNotesSchema = z
    .string()
    .trim()
    .max(NOTES_MAX)
    .transform((v) => (v === '' ? null : v));
/**
 * إضافة حركة: one kind and one amount > 0 with at most five decimals (D14,
 * never rounded). تاريخ المعاملة defaults to now on the server; it may not be
 * in the future nor before the account's last locked movement (section 3).
 */
export const createMovementSchema = z.object({
    kind: enteredMovementKindSchema,
    amount: amountInputSchema,
    occurredAt: isoDateTimeSchema.optional(),
    statement: movementStatementSchema.nullish(),
    notes: movementNotesSchema.nullish(),
});
/** تعديل الحركة: the same fields; the kind may change. */
export const updateMovementSchema = z
    .object({
    kind: enteredMovementKindSchema.optional(),
    amount: amountInputSchema.optional(),
    occurredAt: isoDateTimeSchema.optional(),
    statement: movementStatementSchema.nullish(),
    notes: movementNotesSchema.nullish(),
})
    .strict()
    .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'at least one field is required',
});
/** The latest WhatsApp message of a movement (حالة الرسالة). */
export const movementMessageSchema = z.object({
    id: z.string(),
    status: messageStatusSchema,
    attempt: z.number().int().positive(),
    error: z.string().nullable(),
});
/** A statement row (debts.md section 7). */
export const movementSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    legacyId: z.number().int().nullable(),
    reference: z.string(),
    accountId: z.string(),
    kind: movementKindSchema,
    debit: amountSchema,
    credit: amountSchema,
    balanceAfter: amountSchema,
    occurredAt: z.string(),
    statement: z.string().nullable(),
    notes: z.string().nullable(),
    /** Migrated rows: badge مُرحَّلة or تسوية مُرحَّلة, no edit and no delete. */
    locked: z.boolean(),
    revision: z.number().int().positive(),
    /** Null for migrated rows ("—"). */
    createdBy: employeeRefSchema.nullable(),
    createdAt: z.string(),
    /** معدّلة with the last editor and time. */
    edited: z.boolean(),
    editedBy: employeeRefSchema.nullable(),
    editedAt: z.string().nullable(),
    message: movementMessageSchema.nullable(),
});
export const movementSaveResultSchema = z.object({
    movement: movementSchema,
    /** The account's current balance after the change. */
    balance: amountSchema,
    /** How many later movements had their balance recalculated. */
    recalculated: z.number().int().nonnegative(),
});
const statementFilterShape = {
    ...periodQueryShape,
    kind: movementKindFilterSchema.optional(),
};
export const statementQuerySchema = z
    .object({
    ...statementFilterShape,
    /** Absent: the last page, so the latest movements show first. */
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce.number().int().positive().max(200).default(STATEMENT_PAGE_SIZE),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
export const statementExportQuerySchema = z
    .object({
    ...statementFilterShape,
    ...exportLocaleShape,
    format: z.enum(['xlsx', 'pdf']).default('xlsx'),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
/** مجموع المدين، مجموع الدائن and the balance at the end of the period. */
export const statementTotalsSchema = z.object({
    debit: amountSchema,
    credit: amountSchema,
    endBalance: amountSchema,
});
export const statementSchema = z.object({
    account: accountSchema,
    /** الرصيد السابق: the balance before the from-date; null without one. */
    previousBalance: amountSchema.nullable(),
    items: z.array(movementSchema),
    meta: paginationMetaSchema,
    totals: statementTotalsSchema,
});
/**
 * كشف العميل (debts.md section 7.2): the movements of the debtor's accounts
 * shown to the user, merged in movement order; `accountId` narrows it to one
 * of them (الحساب filter, كل الحسابات when absent).
 */
const debtorStatementFilterShape = {
    ...statementFilterShape,
    accountId: z.string().trim().min(1).optional(),
};
export const debtorStatementQuerySchema = z
    .object({
    ...debtorStatementFilterShape,
    /** Absent: the last page, so the latest movements show first. */
    page: z.coerce.number().int().positive().optional(),
    pageSize: z.coerce.number().int().positive().max(200).default(STATEMENT_PAGE_SIZE),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
export const debtorStatementExportQuerySchema = z
    .object({
    ...debtorStatementFilterShape,
    ...exportLocaleShape,
    format: z.enum(['xlsx', 'pdf']).default('xlsx'),
})
    .refine(periodIsOrdered, PERIOD_ORDER_ISSUE);
/** The account of a combined statement row (الحساب، الفرع). */
export const statementAccountSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    name: z.string(),
    branch: branchRefSchema,
});
/**
 * A row of the combined statement: the movement (its `balanceAfter` stays the
 * account's stored balance) with its account and الرصيد, the debtor's running
 * total over the shown accounts, computed when shown.
 */
export const debtorStatementRowSchema = movementSchema.extend({
    account: statementAccountSchema,
    runningBalance: amountSchema,
});
/** أرصدة الحسابات: each shown account with its balance at the end of the period. */
export const accountEndBalanceSchema = z.object({
    account: statementAccountSchema,
    endBalance: amountSchema,
});
export const debtorStatementSchema = z.object({
    debtor: debtorSchema,
    /** الرصيد السابق: the sum of the shown accounts' balances before the from-date; null without one. */
    previousBalance: amountSchema.nullable(),
    items: z.array(debtorStatementRowSchema),
    meta: paginationMetaSchema,
    /** `endBalance` is the sum of `accountBalances`. */
    totals: statementTotalsSchema,
    accountBalances: z.array(accountEndBalanceSchema),
});
/** "سيعاد احتساب رصيد {{count}} حركة لاحقة" before saving (debts.md section 8.1). */
export const laterMovementsQuerySchema = z.object({
    occurredAt: isoDateTimeSchema,
    /** The movement being edited, which is not counted. */
    excludeMovementId: z.string().trim().min(1).optional(),
});
export const laterMovementsSchema = z.object({ count: z.number().int().nonnegative() });
/**
 * Statement suggestions (debts.md section 8.2, glossary section 11): fixed in
 * the application, in this order; the field stays free text.
 */
export const STATEMENT_SUGGESTIONS = {
    DEBIT: [
        { ar: 'أغراض', en: 'Goods' },
        { ar: 'بطاقة', en: 'Card' },
        { ar: 'نقدي', en: 'Cash' },
        { ar: 'ديزل', en: 'Diesel' },
        { ar: 'بنزين', en: 'Petrol' },
        { ar: 'غاز', en: 'Gas' },
        { ar: 'أكل', en: 'Food' },
        { ar: 'دجاج', en: 'Chicken' },
        { ar: 'ماء', en: 'Water' },
        { ar: 'خبز', en: 'Bread' },
        { ar: 'ضيافة', en: 'Hospitality' },
    ],
    CREDIT: [
        { ar: 'دفعة نقدية', en: 'Cash payment' },
        { ar: 'تحويل', en: 'Transfer' },
        { ar: 'محفظة', en: 'Wallet' },
    ],
};
//# sourceMappingURL=movements.js.map